import { resolveWidgetUrl } from "./config.js";
import { isTrustedWidgetMessage } from "./messaging.js";

(() => {
	const scriptTag = document.currentScript as HTMLScriptElement | null;

	// Guards against a double mount (auto-mount + an explicit ChatWidget.mount()
	// call, or the loader script being included twice). Only one iframe is ever
	// created per loader instance.
	let mounted = false;

	async function mount() {
		if (mounted) return;
		mounted = true;

		function domReady(fn: () => void) {
			if (
				document.readyState === "complete" ||
				document.readyState === "interactive"
			) {
				fn();
			} else {
				document.addEventListener("DOMContentLoaded", fn);
			}
		}

		domReady(() => {
			// get embed key from data-embed-key attribute of script tag
			const embedKey = scriptTag?.getAttribute("data-embed-key");
			if (!embedKey) {
				return;
			}

			const widgetBase = resolveWidgetUrl(scriptTag?.src, __WIDGET_URL__);
			if (!widgetBase) {
				console.warn(
					"[keenvoy-widget] loader script src çözülemedi; widget mount edilmedi.",
				);
				return;
			}

			const iframe = document.createElement("iframe");
			// Host origin is the page the loader runs on; the widget needs it to
			// target postMessage instead of using "*".
			const host = encodeURIComponent(window.location.origin);
			const widgetSrc = `${widgetBase}?embedKey=${encodeURIComponent(embedKey)}&host=${host}`;
			const WIDGET_ORIGIN = new URL(widgetSrc).origin;
			iframe.src = widgetSrc;

			// Minimum viable sandbox. allow-same-origin is required so the widget can
			// make credentialed XHR to its own origin and use sessionStorage.
			iframe.setAttribute(
				"sandbox",
				"allow-scripts allow-same-origin allow-forms",
			);

			iframe.style.position = "fixed";
			iframe.style.right = "0px";
			iframe.style.bottom = "0px";
			// Konum widget'tan gelene kadar sağ alt (bugünkü davranış). Tema
			// isteği başarısız olursa ya da tenant bir konum seçmemişse bu kalır.
			function applyPosition(position: "BOTTOM_RIGHT" | "BOTTOM_LEFT") {
				const onLeft = position === "BOTTOM_LEFT";
				iframe.style.right = onLeft ? "auto" : "0px";
				iframe.style.left = onLeft ? "0px" : "auto";
			}
			iframe.style.width = "96px";
			iframe.style.height = "96px";
			iframe.style.border = "0";
			iframe.style.zIndex = "2000";
			document.body.appendChild(iframe);

			// Open size: fullscreen on phones (≤440px, kept in sync with the
			// widget's own breakpoint), otherwise a floating panel anchored
			// bottom-right that still leaves the launcher visible below it.
			function sizeOpenIframe() {
				const isMobile = window.matchMedia("(max-width: 440px)").matches;
				iframe.style.width = isMobile ? "100%" : "min(460px, 100vw)";
				iframe.style.height = isMobile ? "100dvh" : "min(680px, 100dvh)";
			}

			// Re-evaluate the open size on viewport changes (e.g. rotating a
			// phone or resizing the window) so it stays responsive while open —
			// matchMedia evaluated only once at open-time would not.
			let onViewportResize: (() => void) | null = null;

			window.addEventListener("message", (event) => {
				if (!isTrustedWidgetMessage(event, WIDGET_ORIGIN)) {
					return;
				}
				if (event.data.type === "position") {
					applyPosition(event.data.position);
					return;
				}
				if (event.data.state === "open") {
					sizeOpenIframe();
					if (!onViewportResize) {
						onViewportResize = () => sizeOpenIframe();
						window.addEventListener("resize", onViewportResize);
					}
				} else if (event.data.state === "closed") {
					if (onViewportResize) {
						window.removeEventListener("resize", onViewportResize);
						onViewportResize = null;
					}
					iframe.style.width = "96px";
					iframe.style.height = "96px";
				}
			});
		});
	}

	(window as any).ChatWidget = { mount };

	// Single-snippet install: embedding the <script> tag is all a site needs — the
	// widget mounts itself. mount() is idempotent, so an explicit ChatWidget.mount()
	// (for hosts that prefer manual control) stays a safe no-op after this.
	mount();
})();
